import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { generateAIReport } from '../sever/Api';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { loadStripe } from '@stripe/stripe-js';
import ViaReport from './ViaReport';

const TestResultsDashboard = () => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [showComprehensiveReport, setShowComprehensiveReport] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState('');
  // VIA report specific state (fetched from API)
  const [viaReportData, setViaReportData] = useState(null);
  const [viaReportLoading, setViaReportLoading] = useState(false);
  const [viaReportError, setViaReportError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [realtimeUpdate, setRealtimeUpdate] = useState(false);
  const { user } = useAuth();

  // For subscription plans we still use redirectToCheckout elsewhere
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  // Sidebar functionality
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Fetch test results from backend
  const fetchTestResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/test-results');
      if (response.data.testResults) {
        setTestResults(response.data.testResults);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching test results:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Immediate local results update function
  const updateLocalResults = useCallback(() => {
    const localResults = getLocalTestResults();
    if (localResults.length > 0) {
      // Merge with existing results, avoiding duplicates
      setTestResults(prevResults => {
        const existingIds = new Set(prevResults.map(r => `${r.testType}_${r.id}`));
        const newResults = localResults.filter(r =>
          !existingIds.has(`${r.testType}_${r.id}`)
        );

        // Update existing results or add new ones
        const updatedResults = [...prevResults];

        localResults.forEach(localResult => {
          const existingIndex = updatedResults.findIndex(r =>
            r.testType === localResult.testType
          );

          if (existingIndex !== -1) {
            // Update existing result with latest data
            updatedResults[existingIndex] = {
              ...updatedResults[existingIndex],
              ...localResult,
              // Preserve backend ID if it exists
              id: updatedResults[existingIndex].id || localResult.id
            };
          } else {
            // Add new result
            updatedResults.push(localResult);
          }
        });

        return updatedResults;
      });

      console.log('Local results updated:', localResults.length, 'results');
      setLastUpdated(new Date());
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTestResults();
  }, [fetchTestResults]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchTestResults();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchTestResults]);

  // Enhanced real-time update listeners
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && (e.key.startsWith('test_') || e.key === 'testResults' || e.key.includes('_userId'))) {
        console.log('Storage change detected:', e.key);
        // Immediate local update + backend refresh
        updateLocalResults();
        setTimeout(() => {
          fetchTestResults();
        }, 500); // Reduced delay for faster updates
      }
    };

    // Enhanced test completion handler with immediate updates
    const handleTestComplete = (event) => {
      console.log('Test completion detected:', event.detail);

      // Show real-time update indicator
      setRealtimeUpdate(true);

      // Immediate local update
      updateLocalResults();

      // Show notification
      if (event.detail) {
        const { testType } = event.detail;
        console.log(`${testType} test completed - updating results`);
      }

      // Backend refresh with shorter delay
      setTimeout(() => {
        fetchTestResults();
        // Hide update indicator after backend refresh
        setTimeout(() => setRealtimeUpdate(false), 1000);
      }, 500);
    };

    // Listen for custom localStorage changes (for same-tab updates)
    const handleLocalStorageUpdate = (event) => {
      if (event.type === 'localStorageUpdate') {
        console.log('Local storage update detected');
        setRealtimeUpdate(true);
        updateLocalResults();
        setTimeout(() => setRealtimeUpdate(false), 2000);
      }
    };

    // Add all event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('testCompleted', handleTestComplete);
    window.addEventListener('localStorageUpdate', handleLocalStorageUpdate);

    // Also listen for focus events to refresh when user returns to tab
    const handleFocus = () => {
      fetchTestResults();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('testCompleted', handleTestComplete);
      window.removeEventListener('localStorageUpdate', handleLocalStorageUpdate);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchTestResults, updateLocalResults]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchTestResults();
  };



  // Enhanced function to get test results from localStorage
  const getLocalTestResults = () => {
    const results = [];
    const userId = user?._id || user?.id;

    if (!userId) return results;

    // Check for different test types in localStorage
    const testTypes = [
      'bigfive', 'disc', 'ikigai', 'johari', 'mbti', 'enneagram',
      'emotionalintelligence', 'high5', 'viacharacter', 'competency',
      'learningstyle', 'sevenquestion'
    ];

    testTypes.forEach(testType => {
      // Special handling for Johari which stores different keys
      if (testType === 'johari') {
        try {
          const selfSelectionsRaw = localStorage.getItem(`johari_self_${userId}`);
          if (selfSelectionsRaw) {
            const selfSelections = JSON.parse(selfSelectionsRaw) || [];
            const feedbackRaw = localStorage.getItem(`johari_feedback_${userId}`);
            const feedbackObj = feedbackRaw ? JSON.parse(feedbackRaw) : {};
            const allOthersSelections = Object.values(feedbackObj).flat();
            const uniqueOthers = Array.from(new Set(allOthersSelections));
            const overlap = selfSelections.filter(adj => uniqueOthers.includes(adj));
            const blind = uniqueOthers.filter(adj => !selfSelections.includes(adj));
            const agreementPercentage = selfSelections.length > 0
              ? Math.round((overlap.length / selfSelections.length) * 100)
              : 0;

            const scores = {
              overallScore: agreementPercentage,
              agreementPercentage,
              overlapCount: overlap.length,
              selfCount: selfSelections.length,
              othersUniqueCount: uniqueOthers.length,
              blindSpotCount: blind.length,
              personalityType: 'Self–Other Alignment'
            };

            const result = {
              id: `johari_${userId}_${Date.now()}`,
              testType: 'johari',
              completedAt: new Date().toISOString(),
              scores,
              status: 'Completed',
              score: scores.overallScore,
              lastUpdated: new Date().toISOString(),
              source: 'localStorage'
            };

            results.push(result);
          }
        } catch (error) {
          console.error('Error building johari local result:', error);
        }
        return; // skip default handler for johari
      }

      const key = `${testType}_${userId}`;
      const storedData = localStorage.getItem(key);

      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          if (data && data.completed) {
            const result = {
              id: data.id || `${testType}_${userId}_${new Date(data.completedAt || Date.now()).getTime()}`,
              testType: testType,
              completedAt: data.completedAt || data.lastUpdated || new Date().toISOString(),
              scores: data.scores || {},
              status: 'Completed',
              // Use actual score if available, otherwise calculate from scores
              score: data.overallScore || data.scores?.overallScore || calculateOverallScore(data.scores, testType),
              // Add additional metadata
              lastUpdated: data.lastUpdated || data.completedAt,
              source: 'localStorage' // Mark as local data
            };
            results.push(result);
          }
        } catch (error) {
          console.error(`Error parsing ${testType} data:`, error);
        }
      }
    });

    // Sort by completion date (newest first)
    return results.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
  };

  // Helper function to calculate overall score from individual scores
  const calculateOverallScore = (scores, testType) => {
    if (!scores || typeof scores !== 'object') return 75; // Default score

    const numericScores = Object.values(scores).filter(score => typeof score === 'number');

    if (numericScores.length === 0) {
      return 75; // Default if no numeric scores
    }

    // Calculate average of numeric scores
    const average = numericScores.reduce((sum, score) => sum + score, 0) / numericScores.length;
    return Math.round(Math.min(Math.max(average, 0), 100)); // Ensure between 0-100
  };

  // Combine backend and local results
  useEffect(() => {
    if (testResults.length === 0) {
      const localResults = getLocalTestResults();
      if (localResults.length > 0) {
        setTestResults(localResults);
      }
    }
  }, [testResults.length]);

  const getTestDisplayName = (testType) => {
    const testNames = {
      'competency': 'Competency Assessment',
      'bigfive': 'Big Five Personality Test',
      'viacharacter': 'VIA Character Strengths',
      'high5': 'High 5 Strengths Test',
      'emotionalintelligence': 'Emotional Intelligence Test',
      'disc': 'DiSC Assessment',
      'ikigai': 'Ikigai Assessment',
      'johari': 'Johari Window Test',
      'mbti': 'MBTI Personality Test',
      'learningstyle': 'Learning Style Assessment',
      'enneagram': 'Enneagram Test',
      'sevenquestion': 'Seven Question Assessment'
    };
    return testNames[testType] || testType;
  };

  const getTestIcon = (testType) => {
    const testIcons = {
      'competency': '🎯',
      'bigfive': '🧠',
      'viacharacter': '⭐',
      'high5': '🏆',
      'emotionalintelligence': '💝',
      'disc': 'D',
      'ikigai': '🎯',
      'johari': '🪟',
      'mbti': '🧩',
      'learningstyle': '📚',
      'enneagram': '9️⃣',
      'sevenquestion': '❓'
    };
    return testIcons[testType] || '📊';
  };

  const getTestColor = (testType) => {
    const testColors = {
      'competency': 'bg-red-500',
      'bigfive': 'bg-blue-500',
      'viacharacter': 'bg-yellow-500',
      'high5': 'bg-purple-500',
      'emotionalintelligence': 'bg-pink-500',
      'disc': 'bg-cyan-500',
      'ikigai': 'bg-green-500',
      'johari': 'bg-orange-500',
      'mbti': 'bg-indigo-500',
      'learningstyle': 'bg-teal-500',
      'enneagram': 'bg-rose-500',
      'sevenquestion': 'bg-emerald-500'
    };
    return testColors[testType] || 'bg-gray-500';
  };

  const getPersonalityType = (result) => {
    const scores = result.scores || {};
    if (!scores || typeof scores !== 'object') return 'N/A';

    if (result.testType === 'disc') {
      if (scores.personalityType || scores.dominantType) {
        return scores.personalityType || scores.dominantType;
      }
      const letters = ['d', 'i', 's', 'c'];
      let top = null;
      let topVal = -1;
      letters.forEach(l => {
        const v = scores[l];
        const pct = typeof v === 'number' ? v : (v && typeof v === 'object' ? v.percentage : null);
        if (pct !== null && pct !== undefined && pct > topVal) { topVal = pct; top = l; }
      });
      return top ? top.toUpperCase() : 'N/A';
    }
    if (result.testType === 'mbti') {
      return scores.personalityType || 'N/A';
    }
    if (result.testType === 'enneagram') {
      const t = scores.personalityType || scores.enneagramType;
      if (t) return `Type ${t}`;
      const entries = Object.entries(scores).filter(([_, v]) => typeof v === 'number');
      if (entries.length) {
        const [topKey] = entries.sort((a, b) => b[1] - a[1])[0];
        return topKey.startsWith('Type ') ? topKey : `Type ${topKey}`;
      }
      return 'N/A';
    }
    if (result.testType === 'bigfive') {
      const { openness = 0, conscientiousness = 0, extraversion = 0, agreeableness = 0, neuroticism = 0 } = scores;
      const max = Math.max(openness, conscientiousness, extraversion, agreeableness, neuroticism);
      if (max === openness) return 'Open to Experience';
      if (max === conscientiousness) return 'Conscientious';
      if (max === extraversion) return 'Extraverted';
      if (max === agreeableness) return 'Agreeable';
      return 'Neurotic';
    }
    if (result.testType === 'sevenquestion') {
      return scores.personalityType || scores.dominantType || 'N/A';
    }
    const numericEntries = Object.entries(scores).filter(([_, v]) => typeof v === 'number');
    if (numericEntries.length) {
      const [topKey] = numericEntries.sort((a, b) => b[1] - a[1])[0];
      return topKey;
    }
    return scores.personalityType || 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getScoreDisplay = (result) => {
    const pct = result.overallPercentage ?? result.overallScore ?? result.score ?? result.scores?.overallScore;
    if (pct !== undefined && pct !== null && !Number.isNaN(Number(pct))) {
      return `${Math.round(Number(pct))}%`;
    }
    return 'N/A';
  };

  const handleTestClick = async (test) => {
    let detailed = test;
    const id = test.id || test._id;
    if (id) {
      try {
        const detailRes = await api.get(`/api/test-results/${id}`);
        detailed = detailRes?.data?.testResult || test;
      } catch (_) { }
    }
    setSelectedTest(detailed);
    setShowDetailedReport(true);
  };

  const closeDetailedReport = () => {
    setShowDetailedReport(false);
    setSelectedTest(null);
  };

  const openPaywall = () => {
    setShowPaywall(true);
  };

  const closePaywall = () => {
    setShowPaywall(false);
  };

  const openComprehensiveReport = () => {
    setShowDetailedReport(false);
    setShowComprehensiveReport(true);
    if (selectedTest) {
      fetchAIReport(selectedTest);
    }
  };

  const handleViewDetailedReportClick = () => {
    try {
      const userStr = localStorage.getItem('user');
      const userLocal = userStr ? JSON.parse(userStr) : null;
      const plan = userLocal?.plan || 'FREE';
      const role = userLocal?.role || '';
      const reportPaid = localStorage.getItem('reportPaid') === 'true';

      console.log("plan:", plan, "role:", role, "reportPaid:", reportPaid);
      const isAllowed =
        plan === 'STANDARD' ||
        plan === 'PROFESSIONAL' ||
        plan === 'PREMIUM' ||
        role === 'admin';

      if (isAllowed) {
        openComprehensiveReport();
        return;
      }

      if (plan === 'FREE' && reportPaid) {
        localStorage.removeItem('reportPaid');
        console.log("plan:", plan, "role:", role, "reportnoPaid:", reportPaid);
        openComprehensiveReport();
        return;
      }

      // Otherwise → show paywall
      setShowPaywall(true);
    } catch (_) {
      setShowPaywall(true);
    }
  };


  // Reuse existing Stripe checkout flow (plans)
  const startCheckout = async (plan) => {
    try {
      const successUrl = `${window.location.origin}/dashboard?checkout=success&plan=${encodeURIComponent(plan)}`;
      const cancelUrl = `${window.location.origin}/dashboard?checkout=cancelled&plan=${encodeURIComponent(plan)}`;
      const response = await api.post('/api/billing/create-checkout-session', {
        plan,
        successUrl,
        cancelUrl,
      });
      const { id, url } = response.data || {};
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (id && publishableKey) {
        const stripe = await loadStripe(publishableKey);
        const { error } = await stripe.redirectToCheckout({ sessionId: id });
        if (error) {
          console.error('Stripe redirect error:', error);
          if (url) window.location.href = url;
        }
      } else if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Checkout error', error);
      const serverMessage = error?.response?.data?.message;
      const serverError = error?.response?.data?.error;
      alert(serverMessage || serverError || 'Unable to start checkout right now. Please try again later.');
    }
  };

  // One-time $8 report via Stripe Checkout redirect
  const startReportCheckout = async () => {
    try {
      // Return user to the same page and unlock report on success
      const success = new URL(window.location.href);
      success.searchParams.set('reportCheckout', 'success');
      const successUrl = success.toString();

      const cancel = new URL(window.location.href);
      cancel.searchParams.set('reportCheckout', 'cancelled');
      const cancelUrl = cancel.toString();

      // Persist the currently selected test so we can reopen it after checkout
      try {
        const selId = selectedTest?.id || selectedTest?._id || '';
        if (selId) localStorage.setItem('pendingReportTestId', selId);
      } catch (_) {}

      const { data } = await api.post('/api/billing/create-report-checkout-session', { successUrl, cancelUrl });
      const { id, url } = data || {};
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (id && publishableKey) {
        const stripe = await loadStripe(publishableKey);
        const { error } = await stripe.redirectToCheckout({ sessionId: id });
        if (error) {
          console.error('Stripe redirect error:', error);
          if (url) window.location.href = url;
        }
      } else if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Report checkout error', error);
      const serverMessage = error?.response?.data?.message;
      const serverError = error?.response?.data?.error;
      alert(serverMessage || serverError || 'Unable to start checkout. Please try again.');
    }
  };

  // Detect Stripe Checkout return for report and unlock report for this user
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reportCheckout = params.get('reportCheckout');
    if (reportCheckout === 'success') {
      try {
        localStorage.setItem('reportPaid', 'true');
      } catch (_) { }
      setShowPaywall(false);
      // If a test is already selected, open the comprehensive report
      if (selectedTest) {
        openComprehensiveReport();
      } else {
        // If not, try to restore from pending id and open
        (async () => {
          try {
            const pendingId = localStorage.getItem('pendingReportTestId');
            if (pendingId) {
              const detailRes = await api.get(`/api/test-results/${pendingId}`);
              const detailed = detailRes?.data?.testResult;
              if (detailed) {
                setSelectedTest(detailed);
                openComprehensiveReport();
              }
            }
          } catch (_) {}
          try { localStorage.removeItem('pendingReportTestId'); } catch (_) {}
        })();
      }
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('reportCheckout');
      window.history.replaceState({}, '', url.toString());
    }
  }, [selectedTest]);

  const closeComprehensiveReport = () => {
    setShowComprehensiveReport(false);
    setSelectedTest(null);
    setAiReportLoading(false);
    setAiReportError(null);
    setAiReportText('');
    setViaReportData(null);
    setViaReportLoading(false);
    setViaReportError(null);
  };

  const fetchAIReport = async (test) => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      setAiReportText('');

      let detailed = test;
      const id = test.id || test._id;
      if (id) {
        try {
          const detailRes = await api.get(`/api/test-results/${id}`);
          detailed = detailRes?.data?.testResult || test;
        } catch (_) {
          // ignore and fallback to existing test object
        }
      }

      const payload = {
        testType: detailed.testType,
        scores: detailed.scores || test.scores || {},
        answers: detailed.answers || test.answers || [],
        overallScore: detailed.overallScore || detailed.score || test.overallScore || test.score,
        overallPercentage: detailed.overallPercentage || detailed.scores?.overallScore || test.overallPercentage || test.scores?.overallScore || test.score,
        userInfo: {
          id: user?._id || user?.id,
          name: user?.name || user?.fullName || user?.email,
        }
      };

      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      console.error('AI report error:', err);
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };

  // Fetch latest VIA Character Strengths result for detailed report
  useEffect(() => {
    const shouldFetchVia = showComprehensiveReport && selectedTest && selectedTest.testType === 'viacharacter';
    if (!shouldFetchVia) return;

    const getviaTestResult = async () => {
      try {
        setViaReportLoading(true);
        setViaReportError(null);
        const response = await api.get('/api/test-results/via/last');
        const testResult = response?.data?.lastTestResult || response?.data?.testResult || null;
        if (testResult) {
          setViaReportData(testResult);
        } else {
          setViaReportError('No VIA result found.');
        }
      } catch (err) {
        setViaReportError(err?.response?.data?.message || 'Failed to load VIA report data');
      } finally {
        setViaReportLoading(false);
      }
    };

    getviaTestResult();
  }, [showComprehensiveReport, selectedTest]);

  // Filter to show only the latest result for each unique test type
  const getLatestTestResults = () => {
    const testMap = new Map();

    testResults.forEach(test => {
      const testType = test.testType;
      const testDate = new Date(test.completedAt || test.createdAt || 0);

      if (!testMap.has(testType) || testDate > new Date(testMap.get(testType).completedAt || testMap.get(testType).createdAt || 0)) {
        testMap.set(testType, test);
      }
    });

    return Array.from(testMap.values()).sort((a, b) => {
      const dateA = new Date(a.completedAt || a.createdAt || 0);
      const dateB = new Date(b.completedAt || b.createdAt || 0);
      return dateB - dateA;
    });
  };

  // Get filtered results showing only latest test for each type
  const sortedResults = getLatestTestResults();

  if (loading && testResults.length === 0) {
    return (
      <>
        <div className='flex flex-col lg:flex-row min-h-screen w-full overflow-x-hidden'>
          <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          </div>

          {/* Main Content */}
          <div className='ml-64 flex-1 relative'>
            <div className='relative'>
              {/* Navbar */}
              <Navbar toggleSidebar={toggleSidebar} />

              {/* Loading Content */}
              <div className="min-h-screen bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading your test results...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='flex flex-col lg:flex-row min-h-screen w-full overflow-x-hidden'>
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>

        {/* Main Content */}
        <div className='ml-64 flex-1 relative'>
          <div className='relative'>
            {/* Navbar */}
            <Navbar toggleSidebar={toggleSidebar} />

            {/* Results Content */}
            <div className="min-h-screen bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">My Test Results</h1>
                      <p className="text-gray-600">
                        Track your personality development journey • Showing latest result for each test type ({sortedResults.length} unique tests)
                      </p>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-4 mt-4 sm:mt-0">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="autoRefresh"
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <label htmlFor="autoRefresh" className="text-sm text-gray-600">
                          Auto-refresh
                        </label>
                      </div>

                      <button
                        onClick={handleManualRefresh}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </button>
                    </div>
                  </div>

                  {/* Last Updated Info */}
                  {lastUpdated && (
                    <div className="mt-2 text-sm text-gray-500 flex items-center">
                      <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                      {autoRefresh && <span className="ml-2 text-blue-600">• Auto-refresh enabled</span>}
                      {realtimeUpdate && (
                        <span className="ml-2 flex items-center text-green-600 animate-pulse">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          • Live update in progress...
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Test Results Grid */}
                {sortedResults.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📊</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No Test Results Yet</h3>
                    <p className="text-gray-600 mb-6">Complete your first personality test to see results here</p>
                    <Link
                      to="/personality-test"
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Take Your First Test
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedResults.map((result) => (
                      <div
                        key={result.id}
                        onClick={() => handleTestClick(result)}
                        className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
                      >
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold ${getTestColor(result.testType)}`}>
                              {getTestIcon(result.testType)}
                            </div>
                            <span className="text-sm text-gray-500">
                              {formatDate(result.completedAt || result.createdAt)}
                            </span>
                          </div>

                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {getTestDisplayName(result.testType)}
                          </h3>

                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Score:</span>
                              <span className="text-lg font-bold text-blue-600">
                                {getScoreDisplay(result)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Type:</span>
                              <span className="text-sm font-medium text-gray-800">
                                {getPersonalityType(result)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Status:</span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {result.status || 'Completed'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Detailed Report Modal */}
                {showDetailedReport && selectedTest && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-2xl font-bold text-gray-800">
                            {getTestDisplayName(selectedTest.testType)} - Detailed Report
                          </h2>
                          <button
                            onClick={closeDetailedReport}
                            className="text-gray-400 hover:text-gray-600 transition"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Test Type:</span>
                              <p className="text-gray-800">{getTestDisplayName(selectedTest.testType)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Completed:</span>
                              <p className="text-gray-800">{formatDate(selectedTest.completedAt || selectedTest.createdAt)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Score:</span>
                              <p className="text-gray-800">{getScoreDisplay(selectedTest)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Personality Type:</span>
                              <p className="text-gray-800">{getPersonalityType(selectedTest)}</p>
                            </div>
                          </div>

                          {selectedTest.scores && Object.keys(selectedTest.scores).length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-2">Detailed Scores:</h4>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {Object.entries(selectedTest.scores).map(([key, value]) => {
                                    const isNumber = typeof value === 'number';
                                    const isObjWithPct = value && typeof value === 'object' && typeof value.percentage === 'number';
                                    const displayPct = isNumber ? value : isObjWithPct ? value.percentage : null;
                                    const prettyKey = key
                                      .replace(/([A-Z])/g, ' $1')
                                      .replace(/^./, (str) => str.toUpperCase())
                                      .replace(/[-_]/g, ' ');
                                    return (
                                      <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-2">
                                          <h5 className="font-medium text-gray-800">{prettyKey}</h5>
                                          <span className="text-sm font-semibold text-blue-600">
                                            {isNumber ? `${Math.round(value)}%` : isObjWithPct ? `${Math.round(value.percentage)}%` : String(value)}
                                          </span>
                                        </div>
                                        {displayPct !== null && (
                                          <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                              style={{ width: `${Math.min(Math.max(displayPct, 0), 100)}%` }}
                                            ></div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                            <button
                              onClick={closeDetailedReport}
                              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition"
                            >
                              Close
                            </button>
                            <button
                              onClick={handleViewDetailedReportClick}
                              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Detailed Report
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comprehensive Detailed Report Modal */}
                {showComprehensiveReport && selectedTest && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                          <div>
                            <h1 className="text-3xl font-bold text-gray-800">
                              {getTestDisplayName(selectedTest.testType)}
                            </h1>
                            <p className="text-gray-600 mt-1">Comprehensive Test Report</p>
                          </div>
                          <button
                            onClick={closeComprehensiveReport}
                            className="text-gray-400 hover:text-gray-600 transition p-2 rounded-full hover:bg-gray-100"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {selectedTest.testType === 'viacharacter' && (
                          <div className="space-y-8">
                            {viaReportLoading && (
                              <div className="text-gray-600 text-sm">Loading VIA report...</div>
                            )}
                            {viaReportError && (
                              <div className="text-red-600 text-sm">{viaReportError}</div>
                            )}
                            {!viaReportLoading && !viaReportError && (
                              <ViaReport
                                participantName={(viaReportData?.user?.name) || user?.name || user?.fullName || user?.email || 'Participant'}
                                date={formatDate(viaReportData?.completedAt || viaReportData?.createdAt || selectedTest.completedAt || selectedTest.createdAt)}
                                scores={viaReportData?.scores || selectedTest.scores || {}}
                                userInfo={viaReportData?.user || null}
                              />
                            )}
                          </div>
                        )}
                        {selectedTest.testType !== 'viacharacter' && (
                        <>
                        {/* Test Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-blue-600 mb-1">Test Type</h3>
                            <p className="text-lg font-semibold text-blue-800">{getTestDisplayName(selectedTest.testType)}</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-green-600 mb-1">Overall Score</h3>
                            <p className="text-lg font-semibold text-green-800">{getScoreDisplay(selectedTest)}</p>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-purple-600 mb-1">Personality Type</h3>
                            <p className="text-lg font-semibold text-purple-800">{getPersonalityType(selectedTest)}</p>
                          </div>
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-orange-600 mb-1">Completed On</h3>
                            <p className="text-sm font-semibold text-orange-800">{formatDate(selectedTest.completedAt || selectedTest.createdAt)}</p>
                          </div>
                        </div>

                        {/* Detailed Analysis */}
                        <div className="space-y-8">
                          {/* Personality Insights */}
                          <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Personality Insights</h2>
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                              <div className="flex items-center mb-4">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold ${getTestColor(selectedTest.testType)} mr-4`}>
                                  {getTestIcon(selectedTest.testType)}
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-800">{getPersonalityType(selectedTest)}</h3>
                                  <p className="text-gray-600">Based on your {getTestDisplayName(selectedTest.testType)} results</p>
                                </div>
                              </div>
                              {selectedTest.testType === 'disc' && (
                                <p className="text-gray-700">
                                  Your DiSC profile shows your behavioral preferences and how you tend to respond to challenges,
                                  influence others, respond to the pace of environment, and how you respond to rules and procedures.
                                </p>
                              )}
                              {selectedTest.testType === 'bigfive' && (
                                <p className="text-gray-700">
                                  The Big Five personality test measures five major dimensions of personality: Openness, Conscientiousness,
                                  Extraversion, Agreeableness, and Neuroticism. Your results provide insights into your behavioral patterns.
                                </p>
                              )}
                              {selectedTest.testType === 'mbti' && (
                                <p className="text-gray-700">
                                  Your MBTI type indicates your preferences for how you focus your attention, take in information,
                                  make decisions, and organize your life. Understanding your type can help in personal and professional development.
                                </p>
                              )}
                              {selectedTest.testType === 'ikigai' && (
                                <p className="text-gray-700">
                                  Ikigai represents your reason for being - the intersection of what you love, what you're good at,
                                  what the world needs, and what you can be paid for. Your assessment reveals your life's purpose.
                                </p>
                              )}
                              {!['disc', 'bigfive', 'mbti', 'ikigai'].includes(selectedTest.testType) && (
                                <p className="text-gray-700">
                                  This assessment provides valuable insights into your personality traits and behavioral patterns,
                                  helping you understand your strengths and areas for development.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Detailed Scores */}
                          {selectedTest.scores && Object.keys(selectedTest.scores).length > 0 && (
                            <div>
                              <h2 className="text-xl font-bold text-gray-800 mb-4">Detailed Score Breakdown</h2>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.entries(selectedTest.scores).map(([key, value]) => {
                                  const isNumber = typeof value === 'number';
                                  const isObjWithPct = value && typeof value === 'object' && typeof value.percentage === 'number';
                                  const displayPct = isNumber ? value : isObjWithPct ? value.percentage : null;
                                  const displayText = isNumber
                                    ? `${Math.round(value)}%`
                                    : isObjWithPct
                                      ? `${Math.round(value.percentage)}%`
                                      : (typeof value === 'string' ? value : JSON.stringify(value));
                                  return (
                                    <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
                                      <div className="flex justify-between items-center mb-2">
                                        <h4 className="font-medium text-gray-800 capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                        </h4>
                                        <span className="text-lg font-bold text-blue-600">
                                          {displayText}
                                        </span>
                                      </div>
                                      {(displayPct !== null) && (
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${Math.min(Math.max(displayPct, 0), 100)}%` }}
                                          ></div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* AI Detailed Report */}
                          {/* <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4">We are collecting your results</h2>
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              {aiReportLoading && (
                                <div className="flex items-center text-gray-600">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                                  Generating your personalized report...
                                </div>
                              )}
                              {aiReportError && (
                                <div className="text-red-600 text-sm">{aiReportError}</div>
                              )}
                              {!aiReportLoading && !aiReportError && aiReportText && (
                                <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                              )}
                              {!aiReportLoading && !aiReportError && !aiReportText && (
                                <button
                                  onClick={() => fetchAIReport(selectedTest)}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                                >
                                  Generate Report
                                </button>
                              )}
                            </div>
                          </div> */}

                          {/* Recommendations */}
                          <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Recommendations for Growth</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="bg-green-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Strengths to Leverage
                                </h3>
                                <ul className="text-green-700 space-y-2">
                                  <li>• Continue developing your natural talents</li>
                                  <li>• Use your strengths in team collaborations</li>
                                  <li>• Seek roles that align with your preferences</li>
                                  <li>• Share your insights with others</li>
                                </ul>
                              </div>
                              <div className="bg-orange-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  Areas for Development
                                </h3>
                                <ul className="text-orange-700 space-y-2">
                                  <li>• Practice skills outside your comfort zone</li>
                                  <li>• Seek feedback from diverse perspectives</li>
                                  <li>• Set goals for personal growth</li>
                                  <li>• Consider mentoring or coaching</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Action Items */}
                          <div className="bg-gray-50 p-6 rounded-lg">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Next Steps</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="text-center">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                </div>
                                <h3 className="font-semibold text-gray-800 mb-2">Learn More</h3>
                                <p className="text-gray-600 text-sm">Take additional assessments to gain deeper insights</p>
                              </div>
                              <div className="text-center">
                                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                </div>
                                <h3 className="font-semibold text-gray-800 mb-2">Share Results</h3>
                                <p className="text-gray-600 text-sm">Discuss with mentors, coaches, or team members</p>
                              </div>
                              <div className="text-center">
                                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                </div>
                                <h3 className="font-semibold text-gray-800 mb-2">Track Progress</h3>
                                <p className="text-gray-600 text-sm">Retake assessments periodically to measure growth</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        </>
                        )}

                        {/* Footer Actions */}
                        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                          <button
                            onClick={closeComprehensiveReport}
                            className="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition"
                          >
                            Close Report
                          </button>
                          <div className="flex gap-3">
                            {/* <button
                              onClick={() => window.print()}
                              className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                              Print Report
                            </button> */}
                            <Link
                              to="/dashboard"
                              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                              Back to Dashboard
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paywall Modal */}
                {showPaywall && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ height: '100vh', overflow: 'auto' }}>
                    <div
                      className="absolute inset-0 bg-black/50"
                      onClick={closePaywall}
                    />
                    <div className="relative z-10 w-[92%] max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
                      <button
                        className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                        onClick={closePaywall}
                      >
                        ✕
                      </button>

                      <div className="text-center p-6">
                        <h2 className="text-xl font-bold">Unlock Detailed Report</h2>
                        <p className="text-sm text-gray-600">Choose a plan that suits you best</p>
                      </div>

                      <div className="space-y-4 px-6 pb-6">
                        <div className="rounded-lg bg-gradient-to-r from-orange-400 to-yellow-400 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={startReportCheckout}>
                          <span className="font-semibold">Get Your Complete Report</span>
                          <span className="text-lg font-bold">$8</span>
                        </div>

                        <div className="rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('STANDARD')}>
                          <span className="font-semibold">Standard</span>
                          <span className="text-lg font-bold">$15</span>
                        </div>

                        <div className="rounded-lg bg-gradient-to-r from-purple-400 to-pink-500 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('PROFESSIONAL')}>
                          <span className="font-semibold">All-Access Professional</span>
                          <span className="text-lg font-bold">$144.99</span>
                        </div>

                        <div className="rounded-lg bg-gradient-to-r from-pink-400 to-red-500 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('PREMIUM')}>
                          <span className="font-semibold">Premium Plus</span>
                          <span className="text-lg font-bold">$199.99</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TestResultsDashboard;
